import { type SearchScope } from '@bizzdesign/sdk-search';
import { assertIsUUID, type UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  type ContentSelection,
  type FilterGlobalStore,
  type FilterItem,
  type SearchParameters,
  type SearchResultsGlobalStore,
  searchResultToObjectDto,
  type SearchService,
} from '@bizzdesign/data-access-search';
import { type UsersService } from '@bizzdesign/data-access-users';
import {
  type AdvancedFilter,
  type ObjectDto,
  type ObjectSearchResultDto,
  type SdkSystemAttributesFilter,
  type SystemAttributesUser,
} from '@bizzdesign/shared/models';
import { isEmpty } from 'lodash-es';
import { map, type Observable, of, switchMap } from 'rxjs';

// Tests are not added for this utils as its already been tested in component store
export function handleCountSearchResults(
  advancedFilter: AdvancedFilter,
  filterGlobalStore: FilterGlobalStore,
  searchService: SearchService,
  resultCountHandler: (
    counts: Map<SearchScope, number>,
    advancedFilter: AdvancedFilter,
  ) => void,
): void {
  filterGlobalStore.updateSearchResultsCountLoading(true);
  searchService.countSearchResults({
    searchTerm: filterGlobalStore.searchTerm(),
    advancedFilter,
    sdkSearchFilter: filterGlobalStore.sdkSearchFilter(),
    resultCountHandler: resultCountHandler.bind(resultCountHandler),
    resultCountErrorHandler: () =>
      filterGlobalStore.updateSearchResultsCountLoading(false),
  });
}

export function getSelectedContent(
  searchService: SearchService,
  contentSelection: ContentSelection | undefined,
  searchResultsGlobalStore: SearchResultsGlobalStore,
  filterGlobalStore: FilterGlobalStore,
  cypherSearchId: UUID | null,
  cancelObjectLoading$?: Observable<boolean>,
): Observable<ObjectDto[]> {
  if (contentSelection) {
    if (contentSelection.fetchAllItems) {
      searchResultsGlobalStore.updateAllRemainingSearchResultsLoading(true);

      const searchParams: SearchParameters = {
        searchTerm: filterGlobalStore.searchTerm(),
        advancedFilter: {
          elementTypeFilters:
            filterGlobalStore.advancedFilter().elementTypeFilters,
          attributeFilters: filterGlobalStore.advancedFilter().attributeFilters,
          scopeFilters: ['OBJECT'],
          systemAttributesFilter:
            filterGlobalStore.advancedFilter().systemAttributesFilter,
          relationFilters: filterGlobalStore.advancedFilter().relationFilters,
        },
        sdkSearchFilter: filterGlobalStore.sdkSearchFilter(),
        // TODO: pass attributeCategoryIds
        withAttributes: true,
      };

      const objects$ = cypherSearchId
        ? searchService.fetchAllObjectsFromCypherSearch(
            cypherSearchId,
            searchResultsGlobalStore.totalObjectsCount(),
          )
        : searchService.fetchAllObjectsWithCurrentFilters(
            searchParams,
            cancelObjectLoading$,
          );

      return objects$.pipe(
        map((objects) => {
          const unselectedItemIds = new Set<string>(
            (contentSelection.unselectedItems ?? []).map((it) => it.id),
          );

          searchResultsGlobalStore.updateAllRemainingSearchResultsLoading(
            false,
          );

          return objects
            ? objects
                .filter((item) => !unselectedItemIds.has(item.id ?? ''))
                .map((item) =>
                  searchResultToObjectDto(item as ObjectSearchResultDto),
                )
            : [];
        }),
      );
    } else {
      searchResultsGlobalStore.updateAllRemainingSearchResultsLoading(false);
      return of(
        contentSelection.selectedItems.map((resultItem) =>
          searchResultToObjectDto(
            resultItem.rawResult as ObjectSearchResultDto,
          ),
        ),
      );
    }
  } else {
    searchResultsGlobalStore.updateAllRemainingSearchResultsLoading(false);
    return of([]);
  }
}

export function isObjectFilterItemSelected(filter: FilterItem): boolean {
  return (
    !!filter.selected &&
    (filter.type.includes('elementType') ||
      filter.type.includes('attribute') ||
      filter.type.includes('relation'))
  );
}

export function getSystemAttributesUser(
  systemAttributesFilter: SdkSystemAttributesFilter | undefined,
  usersService: UsersService,
): Observable<SystemAttributesUser | undefined> {
  if (isEmpty(systemAttributesFilter)) {
    return of(undefined);
  }
  const id = systemAttributesFilter.createdBy;
  assertIsUUID(id);

  return usersService
    .searchUsers({
      ids: [id],
      limit: 1,
    })
    .results.pipe(
      switchMap((users) => {
        if (users.length === 0) {
          return of(undefined);
        }
        return of(users[0]);
      }),
    );
}

export function isObjectFilterAddedOrUpdated(
  newAdvancedFilter: AdvancedFilter,
  advancedFilter: AdvancedFilter,
): boolean {
  if (
    newAdvancedFilter.elementTypeFilters.some(
      (type) => !advancedFilter.elementTypeFilters.includes(type),
    )
  ) {
    return true;
  }

  if (
    newAdvancedFilter.attributeFilters.length >
    advancedFilter.attributeFilters.length
  ) {
    return true;
  } else {
    const updated = newAdvancedFilter.attributeFilters.some((newAttrFilter) => {
      const matchingOldAttrFilter = advancedFilter.attributeFilters.find(
        (oldAttrFilter) => oldAttrFilter.id === newAttrFilter.id,
      );
      return (
        !matchingOldAttrFilter ||
        JSON.stringify(matchingOldAttrFilter) !== JSON.stringify(newAttrFilter)
      );
    });
    if (updated) {
      return true;
    }
  }

  if (
    newAdvancedFilter.relationFilters.length >
    advancedFilter.relationFilters.length
  ) {
    return true;
  } else {
    const updated = newAdvancedFilter.relationFilters.some((newRelFilter) => {
      const matchingOldRelFilter = advancedFilter.relationFilters.find(
        (oldRelFilter) => oldRelFilter.id === newRelFilter.id,
      );
      return (
        !matchingOldRelFilter ||
        JSON.stringify(matchingOldRelFilter) !== JSON.stringify(newRelFilter)
      );
    });
    if (updated) {
      return true;
    }
  }

  return false;
}
